import React, { useState, useEffect } from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import { withRouter } from '../../common/withRouter';
import { Loader, ButtonOBSOLETED, DataDefinitionList, Notification, Message } from "../../web.common";
import { getQueryStringParameters } from "../../common/utils";
import { DisplayDate, DisplayGuid } from "../items";
import { LayoutContext } from "../layout/siteLayout";
import PageTop from "../layout/PageTop";
import withApi from "../higherOrderComponents/withApi";
import { DetailsRightBar, NoLink, Divider, Action } from "./DetailsRightBar";
import AriaModal from "react-aria-modal";
import AsyncSelect from "react-select/async";

const ProviderAdminDetailsInternal = ({
    api,
    history,
    match: {
        params: { providerAdminId },
    }
}) => {
    const [loadedData, setLoadedData] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [isOpeningModal, setIsOpeningModal] = useState(false);
    const [isAddingProvider, setIsAddingProvider] = useState(false);
    const [isAddingFacility, setIsAddingFacility] = useState(false);
    const [provider, setProvider] = useState(null);
    const [providers, setProviders] = useState([]);
    const [facility, setFacility] = useState(null);
    const [facilities, setFacilities] = useState([]);
    const [errorMessage, setErrorMessage] = useState(null);

    const navigationButtonStyle = {
        width: "6%",
        color: "grey",
        fontSize: "0.85rem",
    };

    const clickable = {
        cursor: "pointer",
    };

    useEffect(() => {
        async function loadFromApi() {
            loadData()
        }
        if (!loadedData) {
            loadFromApi();
        }
    }, []);

    const loadData = async () => {
        setIsLoading(false);
        const data = await api.providerAdmin.getProviderAdminDetailsById(providerAdminId);
        if (data.success) {
            setLoadedData(data.data);
        }

        setIsLoading(true);
    };

    const lookupProvider = async (value) => {
        const response = await api.providers.lookupByNameOrId(value);
        return response;
    }

    const lookupFacility = async (value) => {
        const response = await api.facilities.lookupByNameOrId(value);
        return response;
    };

    const handleProviderSelection = (selection) => {
        setProvider(selection);
        setProviders([...providers, selection]);
    }

    const handleFacilitySelection = (selection) => {
        setFacility(selection);
        setFacilities([...facilities, selection]);
    }

    const addProviders = async (providers, providerAdminId) => {
        const data = await api.providerAdmin.addProviders(providers, providerAdminId);
        if (data && data.success) {
            loadData();
            providers.length > 1 && setErrorMessage(new Message('success', 'Providers have been added'));
            providers.length === 1 && setErrorMessage(new Message('success', 'Provider has been added'));
        } else {
            providers.length > 1 && setErrorMessage(new Message('error', 'Unable to add providers'));
            providers.length === 1 && setErrorMessage(new Message('error', 'Unable to add provider'));
        }
        defaultData();
    }
    
    const addFacilities = async (facilities, providerAdminId) => {
        const data = await api.providerAdmin.addFacilities(facilities, providerAdminId);
        if (data && data.success) {
            loadData();
            facilities.length > 1 && setErrorMessage(new Message('success', 'Facilities have been added'));
            facilities.length === 1 && setErrorMessage(new Message('success', 'Facility has been added'));
        } else {
            facilities.length > 1 && setErrorMessage(new Message('error', 'Unable to add facilities'));
            facilities.length === 1 && setErrorMessage(new Message('error', 'Unable to add facilities'));
        }
        defaultData();
    }

    const removeProviderAdmin = async (providerAdminId) => {
        const data = await api.providerAdmin.removeUserFromProviderAdmin(providerAdminId);
        if (data && data.success) {
            history.push("/providerAdmin");
            setErrorMessage(new Message('success', 'User has been removed as admin'));

        } else {
            setErrorMessage(new Message('error', 'Unable to remove user as admin'));
        }
    }

    const removeProvider = async (providerId, providerAdminId) => {
        const data = await api.providerAdmin.removeProvider(providerId, providerAdminId);
        if (data && data.success) {
            loadData();
            setErrorMessage(new Message('success', 'Provider has been removed'));
        } else {
            setErrorMessage(new Message('error', 'Unable to remove provider'));
        }
    }

    const removeFacility = async (facilityId, providerAdminId) => {
        const data = await api.providerAdmin.removeFacility(facilityId, providerAdminId);
        if (data && data.success) {
            loadData();
            setErrorMessage(new Message('success', 'Facility has been removed'));
        } else {
            setErrorMessage(new Message('error', 'Unable to remove facility'));
        }
    }

    const removeFacilityToTheList = (facilityId) => {
        const newFacilities = facilities.filter(facility => facility.value !== facilityId);
        setFacilities(newFacilities);
    }

    const removeProviderToTheList = (providerId) => {
        const newProviders = providers.filter(provider => provider.value !== providerId);
        setProviders(newProviders);
    }

    const defaultData = () => {
        setIsOpeningModal(false);
        setIsAddingFacility(false);
        setIsAddingProvider(false);
        setProvider(null);
        setFacility(null);
        setProviders([]);
        setFacilities([]);
    }

    return (
        <LayoutContext.Consumer>
            {() => (
                <React.Fragment>
                    <Notification newMessage={errorMessage} />
                    <div className="content-with-right-pane-delimiter">
                        <PageTop
                            documentTitle="Provider Admin Details"
                            documentDescription="Provider Admin Details"
                        />
                        <h2 className="main-page-header">
                            Provider Admin Details 
                        </h2>
                        <h4 className="main-page-sub-header">{providerAdminId}</h4>
                        
                        {loadedData == null ? (
                            <Loader hasLoaded={false} />
                        ) : (
                            <>
                                {isOpeningModal  && (
                                    <AriaModal
                                        titleText={isAddingProvider ? "Add Provider" : "Add Facility"}
                                        mounted
                                        verticallyCenter={false}
                                        focusDialog
                                        getApplicationNode={() => document.getElementById("app")}
                                        scrollDisabled
                                        dialogStyle={{
                                            width: '400px',
                                            maxWidth: '90%',
                                            height: '500px',
                                            maxHeight: '90%',
                                            overflowY: 'auto',
                                            padding: '20px',
                                        }}
                                    >
                                        <div>
                                            <h3>{isAddingProvider ? "Add Provider" : "Add Facility"} to {loadedData.userName}</h3>
                                            <br />
                                            <div style={{ display: "table", width: "100%" }}>
                                                <label
                                                    style={{
                                                        display: "table-cell",
                                                        width: "30%",
                                                        whiteSpace: "nowrap",
                                                        fontWeight: "bold",
                                                    }}
                                                >
                                                    {isAddingProvider ? "Provider" : "Facility"}
                                                </label>
                                                <span
                                                    style={{ display: "table-cell", padding: "0 4px 0 6px" }}
                                                >
                                                    <AsyncSelect
                                                        className="react-select-filters-section"
                                                        classNamePrefix="react-select-filters-section-prefix"
                                                        defaultOptions
                                                        placeholder={isAddingProvider ? "Search for provider" : "Search for facility"}
                                                        value={isAddingProvider ? provider : facility}
                                                        cacheOptions
                                                        onChange={(selection) =>
                                                            {
                                                                console.log(selection)
                                                                isAddingProvider
                                                                    ? handleProviderSelection(selection)
                                                                    : handleFacilitySelection(selection)
                                                            }
                                                        }
                                                        loadOptions={async (inputValue) => {
                                                            const results = isAddingProvider
                                                                ? await lookupProvider(inputValue)
                                                                : await lookupFacility(inputValue);
                                                                
                                                                return results;
                                                        }}
                                                    ></AsyncSelect>
                                                </span>
                                            </div>
                                            <hr />
                                        </div>
                                        {providers.length > 0 && (
                                            <div>
                                                <h4>Selected Providers</h4>
                                                <br />
                                                <ul>
                                                    {providers.map((provider, index) => (
                                                        <>
                                                            <li key={provider.value}>
                                                                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                                                    <div>{provider.label}</div>
                                                                    <div>
                                                                        <button onClick={() => removeProviderToTheList(provider.value)}>
                                                                            delete
                                                                        </button>
                                                                    </div>
                                                                </div>
                                                            </li>
                                                            <hr />
                                                        </>
                                                    ))}
                                                </ul>
                                            </div>
                                        )}
                                        {facilities.length > 0 && (
                                            <div>
                                                <h4>Selected Facilities</h4>
                                                <ul>
                                                    {facilities.map((facility, index) => (
                                                    <>
                                                        <li key={facility.value}>
                                                            <div style={{display: 'flex', justifyContent:'space-between'}}>
                                                                <div>{facility.label}</div>
                                                                <div>
                                                                    <button onClick={() => removeFacilityToTheList(facility.value)}>
                                                                        delete
                                                                    </button>
                                                                </div>
                                                            </div>
                                                        </li>
                                                        <hr />
                                                    </>
                                                    ))}
                                                </ul>
                                            </div>
                                        )}
                                        <ButtonOBSOLETED
                                            size="small"
                                            onClick={async () => {
                                                if (providers.length> 0) {
                                                    addProviders(providers, loadedData.providerAdminId);
                                                } else if (facilities.length > 0) {
                                                    addFacilities(facilities, loadedData.providerAdminId);
                                                }
                                            }}
                                            disabled={facilities.length === 0 && providers.length === 0}
                                            buttonContent="Add"
                                            ariaLabel="return"
                                        ></ButtonOBSOLETED>
                                        <ButtonOBSOLETED
                                            buttonColor="gray"
                                            size="small"
                                            onClick={() => {
                                                defaultData();
                                            }}
                                            disabled={false}
                                            buttonContent="Back"
                                            ariaLabel="return"
                                        ></ButtonOBSOLETED>
                                    </AriaModal>
                                )}
                                <DataDefinitionList
                                    listData={{
                                            'Provider Admin Id': loadedData.providerAdminId,
                                            'User Id': <DisplayGuid baseAnchor={"../user"}>{loadedData.userId}</DisplayGuid>,
                                            'User': <>{loadedData.userName} - {loadedData.userEmail} </>,
                                    }}
                                    classNames={['detail-page-list']}
                                />
                                <hr />
                                <h4 className="main-page-sub-header">Providers</h4>
                                <article className="data-table-wrap">
                                    <table className="data-lists component-data-table cell-content-align-top">
                                        <thead>
                                            <tr>
                                                <th>Provider Id</th>
                                                <th>Provider Name</th>
                                                <th>Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {loadedData.providers.length > 0 && loadedData.providers.map((provider, index) => (
                                                <tr className="hover-listview" style={{cursor: 'auto'}} key={provider.providerId}>
                                                    <td><DisplayGuid baseAnchor={'../provider'}>{provider.providerId}</DisplayGuid></td>
                                                    <td>{provider.providerName}</td>
                                                    <td><button onClick={async () => {
                                                        const result = confirm(
                                                            "Are you sure to delete this entry?"
                                                        );
                                                        if (!result) {
                                                            return;
                                                        }

                                                        await removeProvider(provider.providerId, loadedData.providerAdminId);
                                                    }}>delete</button></td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </article>
                                <hr />
                                <h4 className="main-page-sub-header">Facilities</h4>
                                <article className="data-table-wrap">
                                    <table className="data-lists component-data-table cell-content-align-top">
                                        <thead>
                                            <tr>
                                                <th>Facility Id</th>
                                                <th>Facility Name</th>
                                                <th>Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {loadedData.facilities.length > 0 && loadedData.facilities.map((facility, index) => (
                                                <tr className="hover-listview" style={{ cursor: 'auto' }} key={facility.facilityId}>
                                                    <td><DisplayGuid baseAnchor={'../facility'}>{facility.facilityId}</DisplayGuid></td>
                                                    <td>{facility.facilityName}</td>
                                                    <td><button onClick={async () => {
                                                        const result = confirm(
                                                            "Are you sure to delete this entry?"
                                                        );
                                                        if (!result) {
                                                            return;
                                                        }

                                                        await removeFacility(facility.facilityId, loadedData.providerAdminId);
                                                    }}>delete</button></td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </article>
                            </>
                        )}
                    </div>
                    <DetailsRightBar>
                        <Divider>Links</Divider>
                        <NoLink>None</NoLink>
                        <Divider>Actions</Divider>

                        {loadedData && (
                            <>
                                <Action
                                    action={() => {
                                        {
                                            setIsOpeningModal(true)
                                            setIsAddingProvider(true)
                                        } 
                                    }}
                                >
                                    Add Provider
                                </Action>
                                <Action
                                    action={() => {
                                        {
                                            setIsOpeningModal(true)
                                            setIsAddingFacility(true)
                                        }
                                    }}
                                >
                                    Add Facility
                                </Action>
                                <Action
                                    action={async () => {
                                        {
                                            const result = confirm(
                                                `Are you sure to remove ${loadedData.userName} as admin?`
                                            );
                                            if (!result) {
                                                return;
                                            }
                                            await removeProviderAdmin(loadedData.providerAdminId);
                                        }
                                    }}
                                >
                                    Remove As Admin
                                </Action>
                            </>

                        )}
                    </DetailsRightBar>
                </React.Fragment>
            )}
        </LayoutContext.Consumer>
    );
};

const ProviderAdminDetails = withRouter(
    withApi(connect(({ user }) => ({ isLoggedIn: !!user }), {})(ProviderAdminDetailsInternal))
);

ProviderAdminDetails.requiresLogin = true;
ProviderAdminDetails.propTypes = { isLoggedIn: PropTypes.bool };

export default ProviderAdminDetails;
